import React, { useState } from 'react';
import classNames from 'classnames';
import { Form as FinalForm, Field } from 'react-final-form';
import { useIntl, FormattedMessage } from '../../util/reactIntl';
import { required, emailFormatValid } from '../../util/validators';

import css from './ContactForm.module.css';

/**
 * ContactForm — generic contact-us form that POSTs to /api/contact.
 *
 * @param {string} [props.className]
 * @param {boolean} [props.showSubject=true]  — show subject field
 */
const ContactForm = ({ className, showSubject = true }) => {
  const intl = useIntl();
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState(null);

  const requiredMsg = id => required(intl.formatMessage({ id }));
  const emailValid = emailFormatValid(intl.formatMessage({ id: 'ContactForm.emailInvalid' }));

  const handleSubmit = async values => {
    setServerError(null);
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Send failed');
      setSubmitted(true);
    } catch (err) {
      setServerError(err.message);
    }
  };

  if (submitted) {
    return (
      <div className={classNames(css.root, className, css.success)}>
        <FormattedMessage id="ContactForm.success" />
      </div>
    );
  }

  return (
    <div className={classNames(css.root, className)}>
      <FinalForm
        onSubmit={handleSubmit}
        render={({ handleSubmit, submitting }) => (
          <form onSubmit={handleSubmit} className={css.form}>
            {/* Name */}
            <Field name="name" validate={requiredMsg('ContactForm.nameRequired')}>
              {({ input, meta }) => (
                <div className={css.field}>
                  <label className={css.label}>
                    <FormattedMessage id="ContactForm.nameLabel" />
                  </label>
                  <input
                    {...input}
                    type="text"
                    className={classNames(css.input, { [css.inputError]: meta.touched && meta.error })}
                    placeholder={intl.formatMessage({ id: 'ContactForm.namePlaceholder' })}
                  />
                  {meta.touched && meta.error && <span className={css.fieldError}>{meta.error}</span>}
                </div>
              )}
            </Field>

            {/* Email */}
            <Field name="email" validate={v => requiredMsg('ContactForm.emailRequired')(v) || emailValid(v)}>
              {({ input, meta }) => (
                <div className={css.field}>
                  <label className={css.label}>
                    <FormattedMessage id="ContactForm.emailLabel" />
                  </label>
                  <input
                    {...input}
                    type="email"
                    className={classNames(css.input, { [css.inputError]: meta.touched && meta.error })}
                    placeholder={intl.formatMessage({ id: 'ContactForm.emailPlaceholder' })}
                  />
                  {meta.touched && meta.error && <span className={css.fieldError}>{meta.error}</span>}
                </div>
              )}
            </Field>

            {/* Subject (optional) */}
            {showSubject && (
              <Field name="subject">
                {({ input }) => (
                  <div className={css.field}>
                    <label className={css.label}>
                      <FormattedMessage id="ContactForm.subjectLabel" />
                    </label>
                    <input
                      {...input}
                      type="text"
                      className={css.input}
                      placeholder={intl.formatMessage({ id: 'ContactForm.subjectPlaceholder' })}
                    />
                  </div>
                )}
              </Field>
            )}

            {/* Message */}
            <Field name="message" validate={requiredMsg('ContactForm.messageRequired')}>
              {({ input, meta }) => (
                <div className={css.field}>
                  <label className={css.label}>
                    <FormattedMessage id="ContactForm.messageLabel" />
                  </label>
                  <textarea
                    {...input}
                    rows={6}
                    className={classNames(css.textarea, { [css.inputError]: meta.touched && meta.error })}
                    placeholder={intl.formatMessage({ id: 'ContactForm.messagePlaceholder' })}
                  />
                  {meta.touched && meta.error && <span className={css.fieldError}>{meta.error}</span>}
                </div>
              )}
            </Field>

            {serverError && <div className={css.serverError}>{serverError}</div>}

            <button type="submit" disabled={submitting} className={css.submitBtn}>
              {submitting
                ? intl.formatMessage({ id: 'ContactForm.sending' })
                : intl.formatMessage({ id: 'ContactForm.submit' })}
            </button>
          </form>
        )}
      />
    </div>
  );
};

export default ContactForm;
